'use client';

import { useState } from 'react';
import { Bookmark, BookmarkCheck, Loader2, X } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { recordProductEvent } from '@/lib/product-analytics';

interface TrackingEvent {
  carrier: string;
  eventType: string;
  eventClassifierCode: 'ACT' | 'EST' | 'PLN';
  eventDateTime: string;
  transportEventTypeCode?: string;
  equipmentEventTypeCode?: string;
  shipmentEventTypeCode?: string;
  modeOfTransport?: string;
  vesselName?: string;
  voyageNumber?: string;
  UNLocationCode?: string;
  locationName?: string;
  facilityTypeCode?: string;
}

interface TrackingResult {
  reference: string;
  referenceType: string;
  detectedCarrier: string | null;
  carriersQueried: string[];
  events: TrackingEvent[];
  errors: string[];
}

interface RouteStop {
  key: string;
  name: string;
  code?: string;
  status: 'past' | 'current' | 'future';
  vessel?: string;
  voyage?: string;
  departedAt?: string;
  arrivedAt?: string;
}

interface SaveShipmentButtonProps {
  result: TrackingResult;
  route: RouteStop[];
  eta: string | null;
  shipmentName?: string;
  customReference?: string;
  notes?: string;
}

export function SaveShipmentButton({ result, route, eta, shipmentName, customReference, notes }: SaveShipmentButtonProps) {
  const [saved, setSaved] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGate, setShowGate] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  async function handleSave() {
    setError(null);
    setLoading(true);

    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    const origin = route[0];
    const destination = route[route.length - 1];

    if (!user) {
      setLoading(false);
      // Persist payload so post-signup redirect can auto-save it
      sessionStorage.setItem('pending_shipment_save', JSON.stringify({
        reference: result.reference,
        reference_type: result.referenceType,
        carrier: result.detectedCarrier ?? result.events[0]?.carrier ?? null,
        origin_unlocode: origin?.code ?? null,
        destination_unlocode: destination?.code ?? null,
        origin_name: origin?.name ?? null,
        destination_name: destination?.name ?? null,
        eta: eta ?? null,
        events_snapshot: result.events,
        ...(shipmentName ? { shipment_name: shipmentName } : {}),
        ...(customReference ? { custom_reference: customReference } : {}),
        ...(notes ? { notes } : {}),
      }));
      setShowGate(true);
      recordProductEvent('shipment_save_signup_prompted');
      return;
    }

    try {
      const res = await fetch('/api/shipments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reference: result.reference,
          reference_type: result.referenceType,
          carrier: result.detectedCarrier ?? result.events[0]?.carrier ?? null,
          origin_unlocode: origin?.code ?? null,
          destination_unlocode: destination?.code ?? null,
          origin_name: origin?.name ?? null,
          destination_name: destination?.name ?? null,
          eta: eta ?? null,
          events_snapshot: result.events,
          ...(shipmentName ? { shipment_name: shipmentName } : {}),
          ...(customReference ? { custom_reference: customReference } : {}),
          ...(notes ? { notes } : {}),
        }),
      });

      if (res.status === 409) {
        const data = await res.json();
        if (data.error === 'limit_reached') {
          const limit = data.limit ?? 3;
          recordProductEvent('shipment_save_limit_reached', { limit });
          setError(
            limit === 3
              ? "You've reached the 3-shipment limit for free accounts. Upgrade to track up to 30 shipments."
              : `You've reached the ${limit}-shipment limit for your plan.`
          );
          if (limit === 3) setLimitReached(true);
        } else {
          setError('Save failed. Try again.');
        }
        return;
      }

      if (!res.ok) {
        setError('Save failed. Try again.');
        return;
      }

      const data = await res.json().catch(() => ({}));
      setSaved(true);
      if (data.already_saved) {
        setAlreadySaved(true);
      } else {
        setShowConfirmation(true);
        recordProductEvent('shipment_saved', { save_source: 'manual' });
      }
    } catch {
      setError('Save failed. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="save-shipment-wrap">
        {saved ? (
          <button className="save-shipment-btn save-shipment-btn--saved" disabled>
            <BookmarkCheck size={16} />
            {alreadySaved ? 'Already in My Shipments' : 'Saved to My Shipments'}
          </button>
        ) : (
          <button
            className="save-shipment-btn"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="save-shipment-spinner" />
                Saving…
              </>
            ) : (
              <>
                <Bookmark size={16} />
                Add to My Shipments
              </>
            )}
          </button>
        )}
        {error && (
          <div className="save-shipment-error">
            {error}
            {limitReached && (
              <Link href="/pricing" className="save-shipment-upgrade-btn">
                Upgrade to Pro
              </Link>
            )}
          </div>
        )}
      </div>

      {showConfirmation && (
        <div className="save-shipment-confirmation" role="alert">
          Saved — you&apos;ll receive email alerts for delays and arrival.
          <button
            className="save-shipment-confirmation-close"
            onClick={() => setShowConfirmation(false)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {showGate && (
        <div className="save-gate-overlay" onClick={() => setShowGate(false)}>
          <div className="save-gate-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="save-gate-close"
              onClick={() => setShowGate(false)}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <h2 className="save-gate-heading">Track your shipments over time</h2>
            <p className="save-gate-body">
              Save shipment results to your dashboard and manage email alerts.
              Status checks depend on available carrier data.
              Free accounts save up to 3 shipments.
            </p>
            <div className="save-gate-actions">
              <Link href="/shipper-signup" className="save-gate-btn save-gate-btn--primary">
                Sign up as Shipper
              </Link>
              <Link href="/login?redirect=/track" className="save-gate-btn save-gate-btn--secondary">
                Log in
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
